import React from 'react';
import { Dimensions, Platform } from 'react-native';

import { Card, H5, XStack, YStack, SizableText } from 'tamagui';
import { PieChart } from 'react-native-chart-kit';
import { MaterialIcons, FontAwesome } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { formatCurrency, formatPercentage, formatPaymentMethod, formatCardBrand } from '@utils/formatters';
interface PaymentMethodStat {
  method: string;
  count: number;
  revenue: number;
  percentage: number;
}

interface CardBrandStat {
  brand: string;
  count: number;
  revenue: number;
  percentage: number;
}

interface PaymentMethodStatsProps {
  stats: {
    paymentMethods: PaymentMethodStat[];
    cardBrands: CardBrandStat[];
  };
  isLoading?: boolean;
}

const PaymentMethodStats: React.FC<PaymentMethodStatsProps> = ({ stats, isLoading }) => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width - 64; // Account for padding

  // ApexCharts for web - use conditional import
  let UsageDonutChart: any = null;
  if (Platform.OS === 'web') {
    try {
      // Use the same import pattern as ApexChartsComponents.web.tsx
      const apexModule = require('@navigation/screens/MidLevel/SettingsScreens/Components/ApexChartsComponents.web'); // web-only import
      UsageDonutChart = apexModule.UsageDonutChart;
    } catch (error) {
      console.warn('[PaymentMethodStats] UsageDonutChart require failed:', error);
      UsageDonutChart = null;
    }
  }


  if (isLoading) {
    return (
      <Card
        padding="$4"
        backgroundColor={theme ? "#ffffff" : "#1e293b"}
        borderRadius={20}
        shadowColor="#000"
        shadowOffset={{ width: 0, height: 12 }}
        shadowOpacity={theme ? 0.08 : 0.25}
        shadowRadius={24}
        elevation={12}
        borderWidth={1}
        borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
        overflow="hidden"
      >
        <YStack space="$3" alignItems="center" minHeight={200}>
          <H5 color={theme ? "#111827" : "#f9fafb"} fontWeight="bold">
            Payment Methods
          </H5>
          <SizableText color={theme ? "#6b7280" : "#9ca3af"}>
            Loading payment method statistics...
          </SizableText>
        </YStack>
      </Card>
    );
  }

  if (!stats || stats.paymentMethods.length === 0) {
    return (
      <Card
        padding="$4"
        backgroundColor={theme ? "#ffffff" : "#1e293b"}
        borderRadius={20}
        shadowColor="#000"
        shadowOffset={{ width: 0, height: 12 }}
        shadowOpacity={theme ? 0.08 : 0.25}
        shadowRadius={24}
        elevation={12}
        borderWidth={1}
        borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
        overflow="hidden"
      >
        <YStack space="$3" alignItems="center" minHeight={200}>
          <H5 color={theme ? "#111827" : "#f9fafb"} fontWeight="bold">
            Payment Methods
          </H5>
          <MaterialIcons name="payment" size={48} color={theme ? "#d1d5db" : "#6b7280"} />
          <SizableText color={theme ? "#6b7280" : "#9ca3af"} textAlign="center">
            No payment data available
          </SizableText>
        </YStack>
      </Card>
    );
  }

  // Prepare pie chart data
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  const pieData = stats.paymentMethods.map((method, index) => ({  
    name: formatPaymentMethod(method.method),
    population: method.count,
    color: colors[index % colors.length],
    legendFontColor: theme ? "#374151" : "#d1d5db",
    legendFontSize: 12,
  }));
  const formattedPieData = {
    labels: stats.paymentMethods.map(method => formatPaymentMethod(method.method)),
    data: stats.paymentMethods.map(method => method.count),
    colors: stats.paymentMethods.map((_, index) => colors[index % colors.length]),
  };

  const chartConfig = {
    color: (opacity = 1) => theme ? `rgba(55, 65, 81, ${opacity})` : `rgba(229, 231, 235, ${opacity})`,
    labelColor: (opacity = 1) => theme ? `rgba(107, 114, 128, ${opacity})` : `rgba(156, 163, 175, ${opacity})`,
  };

  const getPaymentIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case 'card': return 'credit-card';
      case 'bank_transfer': return 'bank';
      case 'sepa_debit': return 'bank';
      case 'ideal': return 'bank';
      case 'klarna': return 'credit-card';
      case 'alipay': return 'mobile-alt';
      case 'wechat_pay': return 'mobile-alt';
      default: return 'credit-card';
    }
  };

  const getCardIcon = (brand: string) => {
    switch (brand.toLowerCase()) {
      case 'visa': return 'cc-visa';
      case 'mastercard': return 'cc-mastercard';
      case 'amex': return 'cc-amex';
      case 'discover': return 'cc-discover';
      case 'jcb': return 'cc-jcb';
      case 'diners': return 'cc-diners-club';
      default: return 'credit-card';
    }
  };

  const StatRow = ({
    icon,
    iconType = 'FontAwesome',
    label,
    count,
    revenue,
    percentage,
    color
  }: {
    icon: any;
    iconType?: 'FontAwesome' | 'MaterialIcons';
    label: string;
    count: number;
    revenue: number;
    percentage: number;
    color: string;
  }) => {
    const IconComponent = iconType === 'MaterialIcons' ? MaterialIcons : FontAwesome;

    return (
      <XStack alignItems="center" space="$3" paddingVertical="$2">
        <XStack alignItems="center" space="$2" flex={1}>
          <Card
            padding="$1.5"
            backgroundColor={color + '20'}
            borderRadius="$2"
            width={28}
            height={28}
            justifyContent="center"
            alignItems="center"
          >
            <IconComponent name={icon} size={14} color={color} />
          </Card>
          <SizableText size="$3" color={theme ? "#374151" : "#d1d5db"}>
            {label}
          </SizableText>
        </XStack>

        <YStack alignItems="flex-end" space="$0.5">
          <SizableText size="$3" fontWeight="600" color={theme ? "#111827" : "#f9fafb"}>
            {count} ({formatPercentage(percentage, 0)})
          </SizableText>
          <SizableText size="$2" color={theme ? "#6b7280" : "#9ca3af"}>
            {formatCurrency(revenue)}
          </SizableText>
        </YStack>
      </XStack>
    );
  };

  return (
    <Card
      padding="$4"
      backgroundColor={theme ? "#ffffff" : "#1e293b"}
      borderRadius={20}
      shadowColor="#000"
      shadowOffset={{ width: 0, height: 12 }}
      shadowOpacity={theme ? 0.08 : 0.25}
      shadowRadius={24}
      elevation={12}
      borderWidth={1}
      borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
      overflow="hidden"
    >
      <YStack space="$4">
        {/* Header */}
        <H5 color={theme ? "#111827" : "#f9fafb"} fontWeight="bold">
          Payment Methods
        </H5>
        <XStack flexWrap="wrap">
        {/* Pie Chart */}
        {pieData.length > 0 && Platform.OS === 'web' && UsageDonutChart && (
            <YStack flex={1} alignItems="center">
              {
                Platform.OS === 'web' ? (
                  <UsageDonutChart data={formattedPieData} height={180} />
                ) :
                  (
                    <PieChart
              data={pieData}
              width={400}
              height={180}
              chartConfig={chartConfig}
              accessor="population"
              backgroundColor="transparent"
              paddingLeft="15"
              center={[10, 0]}
              hasLegend={false}
            />
                  )
              }

          </YStack>
          )}
        {/* Payment Methods List */}
          <YStack flex={1} space="$1">
          <SizableText size="$3" fontWeight="600" color={theme ? "#374151" : "#d1d5db"}>
            Payment Methods
          </SizableText>
          {stats.paymentMethods.map((method, index) => (
            <StatRow
              key={method.method}
              icon={getPaymentIcon(method.method)}
              label={formatPaymentMethod(method.method)}
              count={method.count}
              revenue={method.revenue}
              percentage={method.percentage}
              color={colors[index % colors.length]}
            />
          ))}
        </YStack>
        </XStack>


        {/* Card Brands if available */}
        {stats.cardBrands.length > 0 && (
          <YStack space="$2">
            <SizableText size="$3" fontWeight="600" color={theme ? "#374151" : "#d1d5db"}>
              Card Brands
            </SizableText>
            {stats.cardBrands.slice(0, 4).map((brand, index) => (
              <StatRow
                key={brand.brand}
                icon={getCardIcon(brand.brand)}
                label={formatCardBrand(brand.brand)}
                count={brand.count}
                revenue={brand.revenue}
                percentage={brand.percentage}
                color={colors[(index + 2) % colors.length]}
              />
            ))}
          </YStack>
        )}
      </YStack>
    </Card>
  );
};

export default PaymentMethodStats;
